/**
 * 90-day roadmap (weeks 1-2 / 3-6 / 7-12 kanban + later) with per-column progress, and progress KPIs: resolved and
 * verified counts over time since the report.
 */
import { Badge, Bars, Card, CardHead, Text } from '@qm/ui';
import type { AdvancedReport } from '@qm/contracts/audit-remediation';
import { progressSeries, progressTotals, roadmapBoard, type RoadmapColumn } from './model';
import { L, useReportText } from './text';
import { severityTone, statusTone } from './tones';

export function RoadmapSection({ report, now, onOpen }: { report: AdvancedReport; now: string; onOpen: (id: string) => void }) {
  const { t, num } = useReportText();
  const board = roadmapBoard(report);
  return (
    <Card aria-labelledby="ar-roadmap">
      <CardHead id="ar-roadmap" title={t('audit-remediation.report.roadmap.title')} sub={t('audit-remediation.report.roadmap.sub')} />
      <div className="qm-ar-kanban">
        {board.columns.map((c) => <KanbanColumn key={c.key} c={c} onOpen={onOpen} />)}
      </div>
      {board.later.total ? (
        <details className="qm-ar-later">
          <summary>{t('audit-remediation.report.roadmap.later', { n: num(board.later.total) })}</summary>
          <KanbanList c={board.later} onOpen={onOpen} />
        </details>
      ) : null}
      <Progress report={report} now={now} />
    </Card>
  );
}

function KanbanColumn({ c, onOpen }: { c: RoadmapColumn; onOpen: (id: string) => void }) {
  const { t, num } = useReportText();
  const headId = `ar-rm-${c.key}`;
  return (
    <section className="qm-ar-kanban__col" aria-labelledby={headId}>
      <h3 id={headId} className="qm-ar-h3">{t(`audit-remediation.report.roadmap.${c.key}`)}</h3>
      <div className="qm-ar-meter" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={c.pct}
        aria-label={t('audit-remediation.report.roadmap.progress', { done: num(c.done), total: num(c.total) })}>
        <span style={{ inlineSize: `${c.pct}%` }} />
      </div>
      <p className="qm-ar-muted">{t('audit-remediation.report.roadmap.progress', { done: num(c.done), total: num(c.total) })}</p>
      {c.items.length ? <KanbanList c={c} onOpen={onOpen} /> : <Text tone="muted">{t('audit-remediation.report.roadmap.empty')}</Text>}
    </section>
  );
}

function KanbanList({ c, onOpen }: { c: RoadmapColumn; onOpen: (id: string) => void }) {
  const { t } = useReportText();
  return (
    <ul className="qm-ar-cards">
      {c.items.map((f) => (
        <li key={f.id} className="qm-ar-card" data-status={f.status}>
          <button type="button" className="qm-ar-link" aria-haspopup="dialog" onClick={() => onOpen(f.id)}><L v={f.title} /></button>
          <div className="qm-ar-inline">
            <Badge tone={severityTone(f.severity)}>{t(`audit-remediation.severity.${f.severity}`)}</Badge>
            <Badge tone={statusTone(f.status)}>{t(`audit-remediation.status.${f.status}`)}</Badge>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Progress({ report, now }: { report: AdvancedReport; now: string }) {
  const { t, num, date } = useReportText();
  const totals = progressTotals(report.findings);
  const series = progressSeries(report, now);
  const labels = series.map((p) => t('audit-remediation.report.progress.week', { n: num(p.week) }));
  return (
    <section className="qm-ar-progress" aria-labelledby="ar-progress">
      <h3 id="ar-progress" className="qm-ar-h3">{t('audit-remediation.report.progress.title')}</h3>
      <dl className="qm-ar-kpis">
        {(['resolved', 'verified', 'inProgress', 'open', 'dismissed', 'accepted', 'regressed'] as const).map((k) => (
          <div key={k}><dt>{t(`audit-remediation.report.progress.${k}`)}</dt><dd>{num(totals[k])}</dd></div>
        ))}
      </dl>
      <div className="qm-ar-progress__charts">
        <figure>
          <Bars values={series.map((p) => p.resolved)} labels={labels} label={t('audit-remediation.report.progress.resolvedChart')} />
          <figcaption className="qm-ar-muted">{t('audit-remediation.report.progress.resolvedChart')}</figcaption>
        </figure>
        <figure>
          <Bars values={series.map((p) => p.verified)} labels={labels} label={t('audit-remediation.report.progress.verifiedChart')} />
          <figcaption className="qm-ar-muted">{t('audit-remediation.report.progress.verifiedChart')}</figcaption>
        </figure>
      </div>
      <table className="qm-sr-only">
        <caption>{t('audit-remediation.report.progress.table')}</caption>
        <thead><tr><th scope="col">{t('audit-remediation.report.progress.weekOf')}</th><th scope="col">{t('audit-remediation.report.progress.resolved')}</th><th scope="col">{t('audit-remediation.report.progress.verified')}</th></tr></thead>
        <tbody>{series.map((p) => <tr key={p.week}><th scope="row">{date(p.start)}</th><td>{num(p.resolved)}</td><td>{num(p.verified)}</td></tr>)}</tbody>
      </table>
    </section>
  );
}
